import { useEffect, useState } from "react";
import styles from "./DoctorCards.module.css";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Select,
  MenuItem,
  Typography,
  TextField,
  Autocomplete,
} from "@mui/material";
import dayjs from "dayjs";
import bydefaultpic from "../../assets/SVG/PlaceholderImage/placeholder.svg";
import clock from "../../assets/doctorcard/clock.png";
import date from "../../assets/doctorcard/date.png";

const DoctorCards = () => {
  const navigate = useNavigate();
  const [doctors, setDoctors] = useState<any[]>([]);
  const [filteredDoctors, setFilteredDoctors] = useState<any[]>([]);
  const [selectedSpecialization, setSelectedSpecialization] =
    useState<string>("All");
  const [mergedSpecializations, setMergedSpecializations] = useState<string[]>(
    []
  );
  const location = useLocation();
  const patientId1 = location.state.patientId
    ? location.state.patientId
    : location.state.mrnpatient;
  const [now, setNow] = useState(dayjs());
  useEffect(() => {
    const interval = setInterval(() => {
      setNow(dayjs());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_BASE_URL}/users/getAssignedDoctores`, {
        headers: {
          accept: "*/*",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      })
      .then((response) => {
        const doctorsData = response.data.data;
        setDoctors(doctorsData);
        setFilteredDoctors(doctorsData);
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
      });
  }, []);

  useEffect(() => {
    // Get unique specializations from doctors
    const doctorSpecs = doctors.map((d) => d.specialization);
    const uniqueSpecs = Array.from(new Set(doctorSpecs))?.filter(
      (spec) => spec
    );

    // Add "All" to the list of specializations
    setMergedSpecializations(["All", ...uniqueSpecs]);
  }, [doctors]);

  useEffect(() => {
    if (selectedSpecialization === "All" || !selectedSpecialization) {
      // Show all doctors if "All" is selected or no specialization is selected
      setFilteredDoctors(doctors);
    } else {
      const filtered = doctors?.filter(
        (doctor) =>
          doctor?.specialization?.toLowerCase() ===
          selectedSpecialization.toLowerCase()
      );
      setFilteredDoctors(filtered);
    }
  }, [selectedSpecialization, doctors]);

  const handleSelectedDoctor = (doctor: any) => {
    const patientId = { ...patientId1, doctor: doctor };
    
    // Save selected doctor to localStorage for future appointments
    if (doctor?._id) {
      localStorage.setItem('lastSelectedDoctorSlot', doctor._id);
    }

    navigate("/patient_Appointment_slot", { state: { patientId } });
  };

  const getAvailableDays = (schedule: any[]) => {
    const daysOfWeek: { [key: string]: string } = {
      monday: "Mon",
      tuesday: "Tue",
      wednesday: "Wed",
      thursday: "Thu",
      friday: "Fri",
      saturday: "Sat",
      sunday: "Sun",
    };

    const availableDays = schedule
      ?.filter((day: any) => day.timingScheedules?.length > 0)
      .map((day: any) => daysOfWeek[day.day.toLowerCase()])
      .join(", ");

    return availableDays;
  };

  return (
    <>
      <Box
        sx={{
          backgroundColor: "white",
          padding: { xs: "8px 12px", sm: "12px 19px" },
          borderRadius: "13px",
          marginBottom: { xs: "8px", sm: "15px" },
          display: { xs: "flex", md: "flex" },
          justifyContent: "space-between",
          alignItems: "center",
          flexDirection: { xs: "column", lg: "row" },
          textAlign: { xs: "center", sm: "left" },
        }}
      >
        <Box className="flex justify-between items-center gap-5 w-full xl:w-fit">
          <Box className="flex flex-col sm:flex-row text-start sm:gap-5">
            <Typography
              variant="h6"
              sx={{
                fontSize: { xs: "14px", sm: "16px" },
                fontWeight: "500",
              }}
            >
              <strong className="text-gray-800">Name:</strong>{" "}
              {patientId1?.patientName}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontSize: { xs: "14px", sm: "16px" },
                fontWeight: "500",
              }}
            >
              <strong className="text-gray-800">MRN:</strong> {patientId1?.mrn}
            </Typography>
          </Box>
          <Autocomplete
            freeSolo
            options={mergedSpecializations}
            value={selectedSpecialization}
            onChange={(event, newValue) =>
              setSelectedSpecialization(newValue || "")
            }
            inputValue={selectedSpecialization}
            onInputChange={(event, newInputValue) =>
              setSelectedSpecialization(newInputValue)
            }
            renderInput={(params: any) => (
              <TextField
                {...params}
                placeholder="All Specializations"
                sx={{
                  width: { xs: "95%", sm: "200px" },
                  borderRadius: "12px",
                  background: "#F5F5FB",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    height: "38px",
                    padding: "0 14px",
                    fontSize: { xs: "14px", sm: "16px" },
                    color: "gray",
                    boxShadow: "none",
                    "& fieldset": { border: "none" },
                  },
                  "& .MuiInputBase-input": {
                    height: "38px",
                    padding: "0 !important",
                  },
                }}
              />
            )}
            sx={{
              minWidth: { xs: "65%", sm: "200px" },
              marginLeft: { xs: "0", sm: "10px" },
            }}
          />
        </Box>
        <Box className="flex items-center me-auto sm:mx-auto gap-5 mt-2 sm:mt-4 xl:mt-0">
          <Typography
            variant="h6"
            sx={{
              fontSize: { xs: "13px", sm: "16px" },
              fontWeight: "500",
              marginBottom: { xs: "10px", sm: "0" },
              display: { xs: "none", sm: "flex" },
              alignItems: "center",
            }}
          >
            <img src={clock} alt="" className="w-8 mx-1" />
            <strong className="me-1">Time :</strong> {now.format("h:mm:ss A")}
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontSize: { xs: "13px", sm: "16px" },
              fontWeight: "500",
              marginBottom: { xs: "10px", sm: "0" },
              display: { xs: "none", sm: "block" },
            }}
          >
            <strong>Day :</strong> {now.format("dddd")}
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontSize: { xs: "13px", sm: "16px" },
              fontWeight: "500",
              marginBottom: { xs: "10px", sm: "0" },
              display: { xs: "flex" },
              alignItems: "center",
            }}
          >
            <img src={date} alt="" className="w-6 sm:w-8 me-1" />
            <strong className="me-1">Date :</strong> {now.format("MMM D, YYYY")}{" "}
            <span className="block sm:hidden font-semibold">
              , {now.format("dddd")}
            </span>
          </Typography>
        </Box>
      </Box>

      {/* Display a message if no doctors are found */}
      {filteredDoctors.length === 0 ? (
        <Typography
          variant="h6"
          sx={{ fontWeight: "600", textAlign: "center", color: "gray" }}
        >
          No doctors available for the selected specialization. Please try a
          different one.
        </Typography>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-6 gap-2 sm:gap-3">
          {filteredDoctors?.map((doctor: any) => (
            <div className={`${styles.card} p-2 sm:p-3`} key={doctor._id}>
              {/* For Desktop View */}
              <div className="hidden sm:block">
                <img
                  src={
                    doctor?.doctorDetails?.photoUrl
                      ? doctor?.doctorDetails?.photoUrl
                      : bydefaultpic
                  }
                  alt=""
                  className={`${styles.card__img} transition`}
                />
                <span
                  className={`${styles.card__span} text-gray-800 text-[16px] leading-6`}
                  style={{ fontFamily: "Arial, sans-serif" }}
                >
                  {doctor?.fullName}{" "}
                </span>
                <span className="text-[14px] flex font-semibold justify-center">
                  {doctor?.specialization
                    ? doctor?.specialization
                    : doctor?.doctorDetails?.specialization}
                </span>
                {/* Show available days of doctor in abbreviated format */}
                <p className={styles.card__info}>{doctor?.designationDetail}</p>
              </div>
              {/* For Mobile View */}
              <div className="flex sm:hidden">
                <img
                  src={
                    doctor?.doctorDetails?.photoUrl
                      ? doctor?.doctorDetails?.photoUrl
                      : bydefaultpic
                  }
                  alt=""
                  className={`${styles.card__img} transition mx-0`}
                />
                <div className="flex flex-col ms-3 w-full">
                  <span
                    className={`${styles.card__span} text-gray-800 text-base me-auto mt-0`}
                  >
                    {doctor?.fullName}
                  </span>
                  <span className="text-sm font-semibold">
                    {doctor?.specialization}
                  </span>
                  {/* Show available days of doctor in abbreviated format */}
                  <p className={`${styles.card__info}`}>
                    {doctor?.designationDetail}
                  </p>
                </div>
              </div>
              {/* For Desktop View */}
              <div className="hidden sm:block ">
                <span className="text-[14px] font-semibold flex justify-center">
                  Available
                </span>

                <div className="text-[12px] mt-1 text-gray-700 font-semibold text-center">
                  {getAvailableDays(doctor?.doctorDetails?.weeklySchedule) ||
                    "No availability"}
                </div>

                <button
                  className={`${styles.card__button}`}
                  onClick={() => handleSelectedDoctor(doctor)}
                >
                  Select
                </button>
              </div>

              {/* For Mobile View */}
              <div className="flex sm:hidden mt-1 justify-between">
                <div className="text-[12px] mt-1 text-gray-700 font-semibold">
                  <span className="text-[14px] font-semibold mt-0.5 mx-1.5">
                    Available:
                  </span>
                  {getAvailableDays(doctor?.doctorDetails?.weeklySchedule) ||
                    "No availability"}
                </div>

                <div>
                  <button
                    className={`${styles.card__button} px-3 py-1 h-fit my-auto text-sm`}
                    onClick={() => handleSelectedDoctor(doctor)}
                  >
                    Select
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default DoctorCards;
